import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { type ExploreApiGateway } from "@modules/explore/gateway/exploreApiGateway"
import {
  ExploreBenchmarkContentDtoSchema,
  ExploreBoxContentDtoSchema,
  type ExploreContentDto,
  ExploreProfileContentDtoSchema,
  ExploreProgamContentDtoSchema,
  ExploreProgramBundleContentDtoSchema,
  ExploreProProgramContentDtoSchema,
} from "@modules/explore/gateway/exploreDto"
import { type Page, type Paginated, type PaginatedWrapper, toPage, wrapperToPage } from "@modules/utils/paginated"
import { compact } from "lodash"

export class ExploreApi implements ExploreApiGateway {
  private baseUrl = "/explore"
  private baseBoxUrl = "/explore/box/pro"

  constructor(private api: ApiClient) {}

  async searchExplore(
    query: string | undefined,
    options?: {
      filter?: ExploreFilter
      page?: number
      size?: number
    },
  ): Promise<Page<ExploreContentDto>> {
    const result = await this.api.get<Paginated<ExploreContentDto>>(this.baseUrl, {
      params: {
        query,
        page: options?.page ?? 0,
        size: options?.size ?? 24,
        filter: options?.filter,
      },
    })
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: compact(
        pageResult.content.flatMap((content) => {
          let parseResult

          if (content.contentType === ExploreFilter.BENCHMARK) {
            parseResult = ExploreBenchmarkContentDtoSchema.safeParse(content)
          }
          if (content.contentType === ExploreFilter.PROGRAM) {
            parseResult = ExploreProgamContentDtoSchema.safeParse(content)
          }
          if (content.contentType === ExploreFilter.PROGRAM_BUNDLE) {
            parseResult = ExploreProgramBundleContentDtoSchema.safeParse(content)
          }
          if (content.contentType === ExploreFilter.BOX) {
            parseResult = ExploreBoxContentDtoSchema.safeParse(content)
          }
          if (content.contentType === ExploreFilter.PROFILE) {
            parseResult = ExploreProfileContentDtoSchema.safeParse(content)
          }
          if (content.contentType === ExploreFilter.PROGRAM_PRO) {
            parseResult = ExploreProProgramContentDtoSchema.safeParse(content)
          }

          if (!parseResult?.success) {
            console.error("Error parsing explore content", parseResult?.error.issues)
            return []
          }
          return parseResult.data
        }),
      ),
    }
  }

  async searchBoxExplore(
    query: string | undefined,
    options?: {
      page?: number
      size?: number
    },
  ): Promise<Page<ExploreContentDto>> {
    const result = await this.api.get<PaginatedWrapper<ExploreContentDto>>(this.baseBoxUrl, {
      params: {
        query,
        page: options?.page ?? 0,
        size: options?.size ?? 24,
      },
    })
    const pageResult = wrapperToPage(result.data)

    return {
      ...pageResult,
      content: compact(
        pageResult.content.flatMap((content) => LoggerService.parseApiDto(ExploreProProgramContentDtoSchema, content)),
      ),
    }
  }
}
